/**
 * Locale store for web — thin re-export from @petci/shared-ui.
 * Catalogs and store factory live in packages/shared-ui/src/locale.ts (single source of truth).
 * Storage adapter: localStorage (web-only; mobile uses AsyncStorage).
 */
"use client";

import { createLocaleStore, makeUseT, localeToTag, CLDR_TAG } from "@petci/shared-ui";

// CLDR html-lang tag per app locale (WCAG 3.1.1). Also consumed by <LangSync/>.
// Re-exported from shared-ui (one source of truth) — mobile's locale lib reads the
// same map, so the two surfaces cannot drift on tag mapping.
export { CLDR_TAG };

export const useLocaleStore = createLocaleStore(() => localStorage);
export const useT = makeUseT(() => useLocaleStore());

/**
 * D12 date-localization tag for the shared format helpers (`formatDate`,
 * `formatMonth`, …). Delegates to shared-ui's `localeToTag` so the en-TC
 * byte-identity rule is defined once and consumed by both surfaces (W2-m);
 * mobile's `useLocaleTag` is the same three lines over its own store instance.
 */
export function useLocaleTag(): string | undefined {
  return localeToTag(useLocaleStore((s) => s.locale));
}

// Change-sync (WCAG 3.1.1): keep <html lang> in step on every locale CHANGE,
// app-wide and independent of any mounted component. The INITIAL hard-load sync
// is owned by <LangSync/>'s mount effect (N-3) — the old module-eval initial
// read lost the pre-rehydration race and stranded a persisted es/ht on lang="en".
// (The subscribe fires only on change, so it never had that race and stays.)
if (typeof document !== "undefined") {
  useLocaleStore.subscribe((state) => {
    document.documentElement.lang = CLDR_TAG[state.locale];
  });
}

export { LOCALES, LOCALE_LABELS, LOCALE_NAMES } from "@petci/shared-ui";
export type { Locale } from "@petci/shared-ui";
